import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";

import ai from "../../../helpers/axios";
import currencyFormatter from "../../../helpers/currencyFormatter";
import CompChargeModal from "../../payments-linking/comp-charge-modal";

const sourceLabelFor = (row) => (row.sourceLabel === "Funds Ledger" ? "Ledger" : (row.sourceLabel || "-"));

const IncidentalsBlock = ({ tripId, trip, onIncompleteChange, readOnly }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [tripSummary, setTripSummary] = useState(null);
  const [compModalState, setCompModalState] = useState({ open: false, row: null });

  const tripTag = trip?.tripTag;

  const fetchData = useCallback(async () => {
    if (!tripId) return;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get(`/api/trips/${tripId}/incidental-charges`);
      setRows(data || []);
    } catch (error) {
      console.error("Error loading incidental charges:", error);
      setRows([]);
    }
  }, [tripId, getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;

    if (!tripTag) return undefined;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data: results } = await ai.auth(token).get("/api/trips/search", {
          params: { q: tripTag, limit: 5 },
        });
        const match = (results?.data || []).find((t) => t.tripTag === tripTag) || null;
        if (!cancelled) setTripSummary(match);
      } catch {
        if (!cancelled) setTripSummary(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, tripTag]);

  const incomplete = useMemo(
    () => rows.some((row) => row.source === "amex" && !row.actionType),
    [rows],
  );

  useEffect(() => {
    onIncompleteChange?.(incomplete);
  }, [incomplete, onIncompleteChange]);

  const netToFirm = useMemo(
    () => rows.reduce((sum, row) => sum + (row.amount || 0), 0),
    [rows],
  );

  const totalComps = useMemo(
    () => rows.reduce((sum, row) => sum + (row.actionType === "comp" ? (row.amountToComp || 0) : 0), 0),
    [rows],
  );

  const handleOpenComp = useCallback((row) => {
    setCompModalState({
      open: true,
      row: {
        _id: row.xeroTransactionId,
        _lineItemID: row.lineItemID,
        transactionDate: row.date,
        cardName: row.sourceLabel,
        contactName: row.contactName,
        categoryLabel: row.categoryLabel,
        reference: row.reference,
        amount: row.amount,
        trip: {
          tripTag,
          firstDepartureDate: tripSummary?.firstDepartureDate,
          lastArrivalDate: tripSummary?.lastArrivalDate,
          routing: tripSummary?.routing,
        },
        clientOrOperator: tripSummary?.clientName,
      },
    });
  }, [tripTag, tripSummary]);

  const handleCloseComp = useCallback(async (refresh = true) => {
    setCompModalState({ open: false, row: null });
    if (refresh) {
      await fetchData();
    }
  }, [fetchData]);

  const openL2Incidentals = () =>
    window.open(`/trips/${tripId}?tab=incidentals`, "_blank", "noopener,noreferrer");

  const renderComps = (row) => {
    if (row.source !== "amex") {
      return "n/a";
    }
    if (!row.actionType) {
      if (readOnly) return "-";
      return (
        <button type="button" className="base-button btn-danger" onClick={() => handleOpenComp(row)}>
          Comp
        </button>
      );
    }
    return currencyFormatter.format(row.actionType === "comp" ? (row.amountToComp || 0) : 0);
  };

  return (
    <div className="tv-block tv-incidentals">
      <div className="tv-block-header">
        <h5 className="tv-block-title">
          Incidentals
          {incomplete && <span className="tv-text-danger"> - Incomplete</span>}
        </h5>

        {!readOnly && (
          <div className="tv-block-actions">
            <button
              type="button"
              className="base-button icon-button icon-button-small evo-blue"
              onClick={openL2Incidentals}
              title="Add/Edit Incidentals"
            >
              <span className="material-icons">add_circle</span>
            </button>
          </div>
        )}
      </div>

      <div className="tv-block-body">
        {rows.length === 0 ? (
          <div className="tv-empty">No incidental charges linked to this trip</div>
        ) : (
          <table className="tv-table tv-incidentals-table">
            <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th>Category</th>
              <th className="text-align-right">Amount</th>
              <th className="text-align-right">Comps</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((row) => (
              <tr key={row._id}>
                <td>{row.date ? moment(row.date).format("M/D/YYYY") : "-"}</td>
                <td>{sourceLabelFor(row)}</td>
                <td>{row.categoryLabel || "-"}</td>
                <td className="text-align-right">{currencyFormatter.format(row.amount || 0)}</td>
                <td className="text-align-right">{renderComps(row)}</td>
              </tr>
            ))}
            </tbody>
            <tfoot>
            <tr>
              <td colSpan={3}><b>Net to Firm</b></td>
              <td className="text-align-right"><b>{currencyFormatter.format(netToFirm)}</b></td>
              <td/>
            </tr>
            <tr>
              <td colSpan={3}><b>Comps</b></td>
              <td/>
              <td className="text-align-right"><b>{currencyFormatter.format(totalComps)}</b></td>
            </tr>
            </tfoot>
          </table>
        )}
      </div>

      <CompChargeModal
        isOpen={compModalState.open}
        mode="comp"
        row={compModalState.row}
        onRequestClose={handleCloseComp}
      />
    </div>
  );
};

export default IncidentalsBlock;
